import { field, readContract, writeContractAndWait } from "../viem";
import MafiaPerkManagerAbi from "../../abis/MafiaPerkManager.json";
import config from "../../config/config";
import { PendingStake } from "../../types/MafiaPerkManager";

const address = config.perkManager;
const abi = MafiaPerkManagerAbi;

export const getUserPendingStakes = async (account: string) => {
  const userPendingStakes: PendingStake[] = await readContract({
    address,
    abi,
    functionName: "getUserPendingStakes",
    args: [account],
    account,
  });

  return userPendingStakes.map((stake) => ({
    user: stake.user,
    stakedAmount: Number(stake.stakedAmount) / 10 ** 18,
    stakingStartTime: Number(stake.stakingStartTime),
    isActive: Boolean(stake.isActive),
  }));
};

export const getUserStakeIds = async (account: string) => {
  const userStakeIds: number[] = await readContract({
    address,
    abi,
    functionName: "getuserStakeIds",
    args: [account],
  });
  return userStakeIds.map((stake) => Number(stake));
};

export const activatePerk = async (
  account: string,
  itemId: number,
  slotIndex: number
) => {
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "activatePerk",
    args: [itemId, slotIndex],
    account,
  });

  return tx.transactionHash;
};

export const cancelPerk = async (account: string, slotIndex: number) => {
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "cancelPerk",
    args: [slotIndex],
    account,
  });

  return tx.transactionHash;
};

export const unlockStake = async (account: string, stakeId: number) => {
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "unlockStake",
    args: [stakeId],
    account,
  });

  return tx.transactionHash;
};

export const cleanupExpiredPerks = async (account: string) => {
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "cleanupExpiredPerks",
    args: [account],
    account,
  });

  return tx.transactionHash;
};

export const getActivePerkInfo = async (
  account: string,
  categoryIds: number[]
) => {
  const results = [];

  for (let i = 0; i < categoryIds.length; i++) {
    const categoryId = categoryIds[i];

    try {
      const result: any = await readContract({
        address,
        abi,
        functionName: "getActivePerkInfo",
        args: [account, categoryId],
      });

      results.push({
        categoryId,
        hasActivePerk: Boolean(field(result, "hasActivePerk", 0)),
        effectStrength: Number(field(result, "effectStrength", 1)),
        duration: Number(field(result, "duration", 2)),
        remainingDuration: Number(field(result, "remainingDuration", 3)),
        slotIndex: Number(field(result, "slotIndex", 4)),
      });
    } catch (error) {
      console.error(
        `Error fetching perk info for category ${categoryId}:`,
        error
      );
    }
  }

  return results;
};

export const getActivePerks = async (account: string) => {
  const perks: any = await readContract({
    address,
    abi,
    functionName: "getActivePerks",
    args: [account],
  });

  const activePerks = field(perks, "activePerks", 0) || [];
  const slotIndices = field(perks, "slotIndices", 1) || [];

  return {
    activePerks: activePerks.map((perk: any) => ({
      itemId: Number(perk.itemId),
      categoryId: Number(perk.categoryId),
      typeId: Number(perk.typeId),
      activationTime: Number(perk.activationTime),
      expirationTime: Number(perk.expirationTime),
      stakedAmount: Number(perk.stakedAmount) / 10 ** 18,
      effectStrength: Number(perk.effectStrength),
      isActive: perk.isActive,
    })),
    slotIndices: slotIndices.map((index: number) => Number(index)),
  };
};

export const getUserPerkStats = async (account: string) => {
  const stats: any = await readContract({
    address,
    abi,
    functionName: "getUserPerkStats",
    args: [account],
  });

  return {
    activePerkCount: Number(stats.activePerkCount),
    totalPerkCount: Number(stats.totalPerkCount),
    totalStaked: Number(stats.totalStaked) / 10 ** 18,
    activeCategories: stats.activeCategories.map((category: any) =>
      Number(category)
    ),
  };
};
